import { useState } from 'react'

interface FAQItem {
  question: string
  answer: string | JSX.Element
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const faqs: FAQItem[] = [
    {
      question: "Is this official DOE guidance?",
      answer: "No. The Genesis Pack is not official DOE documentation or legal/regulatory guidance. It is a developer-focused translation of publicly available DOE/White House materials into actionable engineering workflows."
    },
    {
      question: "Is this legal advice or compliance certification?",
      answer: "No. Harbor Agent provides technical readiness workflows, not legal or regulatory guidance."
    },
    {
      question: "Who is this designed for?",
      answer: (
        <>
          Primarily:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>engineering leads</li>
            <li>ML/Data managers</li>
            <li>security/infra engineers</li>
            <li>product/tech leads planning Genesis participation</li>
          </ul>
          Anyone responsible for technical readiness, reproducibility, governance, or proposal preparation.
        </>
      )
    },
    {
      question: "What exactly do I get for $199?",
      answer: (
        <>
          A complete downloadable toolkit including:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>80–120 item readiness checklist</li>
            <li>reproducibility kits (schemas, metadata standards)</li>
            <li>AI Copilot playbooks</li>
            <li>governance binder templates</li>
            <li>secure submission bundle templates</li>
            <li>12-month roadmap</li>
            <li>automation & audit scripts</li>
            <li>LICENSE.txt personalized to your organization</li>
          </ul>
        </>
      )
    },
    {
      question: "Is this subscription-based?",
      answer: "No. It is a one-time $199 per organization purchase with free 2025 updates."
    },
    {
      question: "How do I access the files?",
      answer: (
        <>
          After checkout, you immediately receive:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>a 24-hour signed download link</li>
            <li>a personalized ZIP file (includes LICENSE.txt)</li>
            <li>an email with your secure link</li>
          </ul>
        </>
      )
    },
    {
      question: "Do you track or fingerprint downloads?",
      answer: (
        <>
          Lightly. We generate:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>a signed token</li>
            <li>per-customer license file</li>
            <li>expiration metadata</li>
          </ul>
          We do not track usage inside your organization.
        </>
      )
    },
    {
      question: "Can multiple people in my org use the pack?",
      answer: "Yes, as long as they are part of the same organization under the same license."
    },
    {
      question: "Can I request a custom version for my org?",
      answer: (
        <>
          Yes — we can create:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>org-specific checklists</li>
            <li>tailored playbooks</li>
            <li>proposal guidance</li>
          </ul>
          Contact: <a href="mailto:support@gentlyventures.com" className="text-primary-600 hover:text-primary-700 underline">support@gentlyventures.com</a>
        </>
      )
    },
    {
      question: "What if my team uses Copilot/Cursor/Claude Code?",
      answer: "Perfect — the pack includes AI-native, IDE-native workflows and playbooks for exactly these tools."
    },
    {
      question: "What if Genesis isn't yet relevant to us?",
      answer: (
        <>
          Use the pack to:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>evaluate future readiness</li>
            <li>modernize pipelines and governance</li>
            <li>prepare your infra for potential DOE/AI/energy-sector partnerships</li>
          </ul>
          It's valuable even before applying.
        </>
      )
    },
    {
      question: "What technical standards does Genesis align with?",
      answer: (
        <>
          The Genesis Mission aligns with several established frameworks:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>NIST AI RMF</strong> — The pack includes guidance aligned with NIST AI Risk Management Framework principles for validity, safety, security, and accountability</li>
            <li><strong>FAIR Data Principles</strong> — Findable, Accessible, Interoperable, and Reusable data standards are emphasized throughout the readiness checklist</li>
            <li><strong>Containerization standards</strong> — Docker/Singularity compatibility for HPC environments</li>
            <li><strong>Workflow portability</strong> — Alignment with Airflow, Nextflow, Prefect, and other scientific workflow engines</li>
          </ul>
          <p className="mt-2 text-sm text-gray-500">
            Sources: <a href="https://www.nist.gov/itl/ai-risk-management-framework" className="underline" target="_blank" rel="noopener noreferrer">NIST AI RMF</a>, <a href="https://www.go-fair.org/fair-principles/" className="underline" target="_blank" rel="noopener noreferrer">FAIR Principles</a>
          </p>
        </>
      )
    },
    {
      question: "How does this relate to other DOE initiatives?",
      answer: (
        <>
          Genesis builds on and integrates with existing DOE programs:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Exascale Computing Project (ECP)</strong> — Genesis leverages exascale infrastructure and lessons learned</li>
            <li><strong>National Laboratory Network</strong> — Genesis connects all 17 DOE National Labs through shared AI and data platforms</li>
            <li><strong>Energy Earthshots Initiative</strong> — Genesis supports clean energy research goals through AI-accelerated discovery</li>
          </ul>
          <p className="mt-2 text-sm text-gray-500">
            The pack helps you align with these broader DOE strategic priorities.
          </p>
        </>
      )
    },
    {
      question: "What file formats and standards are required?",
      answer: (
        <>
          Genesis-aligned systems should support:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Data formats:</strong> JSON, CSV, Parquet, HDF5 for interoperability</li>
            <li><strong>Model formats:</strong> ONNX or equivalent portable formats for model sharing</li>
            <li><strong>Metadata:</strong> Machine-readable schemas (JSON/YAML) with provenance tracking</li>
            <li><strong>Containers:</strong> Docker or Singularity for reproducible deployments</li>
          </ul>
          <p className="mt-2 text-sm text-gray-500">
            The pack includes templates and examples for all these formats.
          </p>
        </>
      )
    }
  ]

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Genesis Pack — FAQ
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Common questions about the Genesis Mission Readiness Pack
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-primary-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="text-lg font-semibold text-primary-700 pr-4">
                    {faq.question}
                  </span>
                  <svg
                    className={`w-5 h-5 text-primary-600 flex-shrink-0 transition-transform duration-200 ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openIndex === index && (
                  <div
                    id={`faq-answer-${index}`}
                    className="px-6 py-4 bg-white text-gray-700 leading-relaxed"
                  >
                    {typeof faq.answer === 'string' ? (
                      <p>{faq.answer}</p>
                    ) : (
                      faq.answer
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

